import { accessApproved, accessDeclined, accessRequested } from "@amtana/access-requests-contract";
import { erasureCancelled, erasureRequested, erasureStepFailed } from "@amtana/account-contract";
import { runCompleted, runFailed, runOutOfCredits } from "@amtana/analysis-contract";
import {
  emailChangeRequested,
  emailVerificationRequested,
  passwordResetRequested,
  takenAddressAttempted,
  userSignedUp,
} from "@amtana/auth-contract";
import {
  creditsLow,
  manualSubscriptionEnded,
  paymentFailed,
  paymentSucceeded,
  rechargeFailed,
  usageThresholdCrossed,
} from "@amtana/billing-contract";
import type { EventDefinition } from "@amtana/module";
import { inviteAccepted, inviteReleased, inviteResent, memberInvited } from "@amtana/organization-contract";

import { deliveryFailed } from "./notification-router.events.ts";

// The router's one catalogue of facts it hears (ADR-022 point 3, S26.05; D994, #110): every
// producer's own event definition, not a second, hand-kept list of strings. Three places used
// to repeat this fact list by hand — Go's `routerFacts` (notification_router.go), the TS rule
// table's own key set (notification-router.rules.ts) and codegen's `routerContractGroups`
// (generate.ts) — with nothing to catch one going stale; a fact missing from one side was
// silently never delivered, the same silence a Google sign-up's missing fact once was (#107).
//
// services/worker/codegen/generate.ts generates Go's `contracts.RouterFacts` straight from this
// array, refusing to run when a fact names an aggregate no Go constant exists for. The TS rule
// table (`notification-router.rules.ts`) types `ROUTER_RULES` as `Record<RoutedFactKey,
// RouterRule>`, built below, so a key missing from, or left over past, this array fails `tsc`.

export const ROUTED_FACTS = [
  userSignedUp,
  emailVerificationRequested,
  passwordResetRequested,
  emailChangeRequested,
  takenAddressAttempted,
  memberInvited,
  inviteResent,
  inviteAccepted,
  inviteReleased,
  accessRequested,
  accessApproved,
  accessDeclined,
  paymentSucceeded,
  paymentFailed,
  deliveryFailed,
  creditsLow,
  usageThresholdCrossed,
  rechargeFailed,
  manualSubscriptionEnded,
  runCompleted,
  runFailed,
  runOutOfCredits,
  erasureRequested,
  erasureCancelled,
  erasureStepFailed,
] as const;

/** `"<aggregate>.<type>"`: the one key format the TS rule table and `/ops`'s own failure reads
 * already use (never Go's own `<aggregate>/<type>`, `eventing.Key`'s own separator). */
export function routedFactKey(definition: Pick<EventDefinition, "aggregate" | "type">): string {
  return `${definition.aggregate}.${definition.type}`;
}

type KeyOf<TDefinition> =
  TDefinition extends EventDefinition<infer TAggregate, infer TType, unknown>
    ? `${TAggregate}.${TType}`
    : never;

/** Every routed fact's own key, as a literal union: `notification-router.rules.ts` types its
 * rule table against this, so a key missing from, or left over past, `ROUTED_FACTS` fails
 * `tsc`, not a silent drop. */
export type RoutedFactKey = KeyOf<(typeof ROUTED_FACTS)[number]>;

type RoutedFactByKey = {
  readonly [TDefinition in (typeof ROUTED_FACTS)[number] as KeyOf<TDefinition>]: TDefinition;
};

/** Every routed fact, by its own key: `notification-router.rules.ts` parses `fact.data`
 * through `ROUTED_FACT_BY_KEY[key].schema` instead of a local zod copy of the same shape. */
export const ROUTED_FACT_BY_KEY: RoutedFactByKey = Object.fromEntries(
  ROUTED_FACTS.map((definition) => [routedFactKey(definition), definition]),
) as RoutedFactByKey;

/** Every routed fact's own key, by itself. */
export const ROUTED_FACT_KEYS: readonly RoutedFactKey[] = ROUTED_FACTS.map(
  (definition) => routedFactKey(definition) as RoutedFactKey,
);
